// ============================================================================
// 💬 CHATVIEW — Vista Completa del Chat (V5 — Scroll Infinito · WhatsApp-Grade)
// src/features/mensagens/components/chat/ChatView.jsx
// ============================================================================
// 🔥 V4: Chat SIEMPRE muestra input — con o sin mensajes previos
// 🔥 V4: Error solo bloquea si NO hay cliente (nunca loading infinito)
// 🔥 V4: isEmpty no bloquea el envío — solo muestra empty state arriba
// 🔥 V5: Scroll infinito hacia arriba — loadOlderMessages()
// 🔥 V5: Mantiene posición de scroll al cargar mensajes antiguos
// 🔥 V5: AnimatePresence con layout animations en eliminaciones
// 🔥 V5: Indicador de "cargando mensajes anteriores"
// ============================================================================

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useIsMobile } from '../../../../hooks/useMediaQuery';
import { useMensagemDetalhe } from '../../hooks/useMensagemDetalhe';
import { useMensagemAcoes } from '../../hooks/useMensagemAcoes';
import { sendWhatsAppMessage } from '../../api/whatsappAdapter';
import { ChatHeader } from './ChatHeader';
import { MessageBubble } from './MessageBubble';
import { ChatInput } from './ChatInput';
import { DateSeparator } from './DateSeparator';
import { SkeletonLoader } from '../shared/SkeletonLoader';
import { EmptyState } from '../lista/EmptyState';
import { MESSAGE_DIRECTION } from '../../utils/constants';
import {
  MessageSquare,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  Loader2,
} from 'lucide-react';

// 🔥 V5: Umbral para detectar scroll al top (px desde arriba)
const SCROLL_TOP_THRESHOLD = 100;

const ChatView = () => {
  // --------------------------------------------------------------------------
  // 📍 HOOKS
  // --------------------------------------------------------------------------
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const {
    message,
    client,
    conversationMessages,
    conversationStats,
    loading,
    error,
    actionLoading,
    // 🔥 V5: Paginación
    hasMore,
    isLoadingOlder,
    loadOlderMessages,
    // Permisos y acciones
    permissions,
    editMessage,
    resendMessage,
    deleteMessage,
    markAsRead,
    copyToClipboard,
    shareMessage,
    downloadMessage,
    goBack,
    isEmpty,
  } = useMensagemDetalhe();

  const {
    deleteConversation,
    toggleArchive,
    toggleStar,
  } = useMensagemAcoes({
    onRefresh: () => {},
  });

  // --------------------------------------------------------------------------
  // 📦 ESTADO LOCAL
  // --------------------------------------------------------------------------
  const [isStarred, setIsStarred] = useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  // --------------------------------------------------------------------------
  // 🔧 REFERENCIAS
  // --------------------------------------------------------------------------
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const prevScrollHeightRef = useRef(0); // 🔥 V5: Para mantener posición al cargar arriba
  const prevMessageCountRef = useRef(0);  // 🔥 V5: Detectar si se agregaron mensajes
  const isLoadingOlderRef = useRef(false); // 🔥 V5: Evitar llamadas duplicadas

  // --------------------------------------------------------------------------
  // 📤 SCROLL AUTOMÁTICO
  // --------------------------------------------------------------------------

  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: smooth ? 'smooth' : 'auto',
      block: 'end',
    });
  }, []);

  // 🔥 V5: Solo auto-scroll si ya estábamos cerca del fondo (no interrumpir lectura)
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 300;

    // Si el usuario está cerca del fondo, hacer scroll automático
    if (isNearBottom && conversationMessages.length > prevMessageCountRef.current) {
      scrollToBottom(false);
    }

    prevMessageCountRef.current = conversationMessages.length;
  }, [conversationMessages.length, scrollToBottom]);

  // 🔥 V5: Mantener posición de scroll al cargar mensajes antiguos
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const prevHeight = prevScrollHeightRef.current;
    const newHeight = container.scrollHeight;

    if (prevHeight > 0 && newHeight > prevHeight && isLoadingOlderRef.current) {
      // Se agregaron mensajes arriba → ajustar scroll para mantener posición
      const heightDiff = newHeight - prevHeight;
      container.scrollTop += heightDiff;
      isLoadingOlderRef.current = false;
    }
  }, [conversationMessages]);

  // 🔥 V5: Detectar scroll al top para cargar mensajes antiguos
  const handleScroll = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    // Mostrar/ocultar botón "bajar"
    setShowScrollButton(scrollHeight - scrollTop - clientHeight > 300);

    // 🔥 V5: Detectar scroll al top → cargar mensajes antiguos
    if (scrollTop <= SCROLL_TOP_THRESHOLD && hasMore && !isLoadingOlder && !isLoadingOlderRef.current) {
      console.log('🔼 [V5] Scroll al top detectado, cargando mensajes antiguos...');
      
      // Guardar altura actual antes de cargar
      prevScrollHeightRef.current = scrollHeight;
      isLoadingOlderRef.current = true;

      loadOlderMessages().then(() => {
        // El useEffect de arriba ajustará el scroll
      });
    }
  }, [hasMore, isLoadingOlder, loadOlderMessages]);

  // --------------------------------------------------------------------------
  // 📤 ENVIAR MENSAJE (WhatsApp real + guardar en Firestore) — V4 con Attachments
  // --------------------------------------------------------------------------

  const handleSend = useCallback(async (data) => {
    if (!client) return;

    // 🔥 V4: Soporta texto simple o objeto { text, attachments }
    const text = typeof data === 'string' ? data : data?.text;
    const attachments = typeof data === 'object' ? data?.attachments : undefined;

    if (!text && !attachments?.length) return;

    setSending(true);
    setSendError(null);

    try {
      await sendWhatsAppMessage({
        to: client.phone,
        message: text || '',
        attachments: attachments || undefined,
        clientId: client.id || conversationId,
        clientName: client.name || client.clientName || 'Cliente',
      });

      // Recargar la conversación para mostrar el nuevo mensaje
      scrollToBottom();
    } catch (err) {
      console.error('❌ [ChatView] Error enviando:', err);
      setSendError(err.message || 'Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  }, [client, conversationId, scrollToBottom]);

  // --------------------------------------------------------------------------
  // 🎯 HANDLERS DE ACCIONES
  // --------------------------------------------------------------------------

  const handleCopy = useCallback(async (msg) => {
    await copyToClipboard(msg);
  }, [copyToClipboard]);

  const handleDelete = useCallback(async (messageId) => {
    await deleteMessage(messageId);
  }, [deleteMessage]);

  const handleForward = useCallback((msg) => {
    setReplyingTo(msg.content?.body || msg.message);
  }, []);

  const handleStar = useCallback(async (star) => {
    if (client?.id) {
      await toggleStar(client.id, star);
      setIsStarred(star);
    }
  }, [client, toggleStar]);

  const handleArchive = useCallback(async (archive) => {
    if (client?.id) {
      await toggleArchive(client.id, archive);
      setIsArchived(archive);
    }
  }, [client, toggleArchive]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // --------------------------------------------------------------------------
  // 📅 AGRUPAR MENSAJES POR FECHA (Más antiguo primero para el render)
  // 🔥 V5: Los mensajes vienen ordenados del más reciente al más antiguo
  //        Se invierten para mostrarlos correctamente en el chat
  // --------------------------------------------------------------------------

  const groupedMessages = React.useMemo(() => {
    if (!conversationMessages.length) return [];

    // 🔥 V5: Invertir para mostrar más antiguo arriba, más reciente abajo
    const orderedMessages = [...conversationMessages].reverse();

    const groups = [];
    let currentLabel = null;
    let currentGroup = [];

    for (const msg of orderedMessages) {
      const timestamp = msg.timestamp || msg.createdAt || 0;
      const date = new Date(timestamp);
      const label = date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });

      if (label !== currentLabel) {
        if (currentGroup.length) {
          groups.push({ label: currentLabel, messages: currentGroup });
        }
        currentLabel = label;
        currentGroup = [msg];
      } else {
        currentGroup.push(msg);
      }
    }

    if (currentGroup.length) {
      groups.push({ label: currentLabel, messages: currentGroup });
    }

    return groups;
  }, [conversationMessages]);

  // --------------------------------------------------------------------------
  // 🎨 RENDER: CARGA (solo si no hay cliente todavía)
  // 🔥 V4: Si hay cliente, NO mostrar skeleton — mostrar chat vacío
  // --------------------------------------------------------------------------

  if (loading && !client) {
    return (
      <div className="flex flex-col h-full bg-[#e5ded4]">
        <ChatHeader client={null} onBack={handleBack} />
        <SkeletonLoader count={6} variant="chat" />
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // 🎨 RENDER: ERROR (solo si NO hay cliente — si hay cliente, mostrar chat)
  // 🔥 V4: Error no bloquea si tenemos cliente cargado
  // --------------------------------------------------------------------------

  if (error && !client) {
    return (
      <div className="flex flex-col h-full bg-[#e5ded4]">
        <ChatHeader client={null} onBack={handleBack} />
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={AlertCircle}
            title="Erro ao carregar conversa"
            description={error}
            action={{
              label: 'Tentar novamente',
              onClick: () => window.location.reload(),
              icon: RefreshCw,
            }}
          />
        </div>
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // 🎨 RENDER PRINCIPAL — Chat (con o sin mensajes)
  // 🔥 V5: Scroll infinito hacia arriba · Animaciones WhatsApp · Carga antigua
  // --------------------------------------------------------------------------

  return (
    <div className="flex flex-col h-full bg-[#e5ded4]">
      {/* Header */}
      <ChatHeader
        client={client}
        conversationStats={conversationStats}
        onBack={handleBack}
        isStarred={isStarred}
        isArchived={isArchived}
        onStar={handleStar}
        onArchive={handleArchive}
      />

      {/* Contenido central */}
      {isEmpty ? (
        // 🔥 V4: Chat vacío — mostrar empty state arriba, input abajo
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={MessageSquare}
            title="Nenhuma mensagem"
            description="Envie a primeira mensagem para começar"
          />
        </div>
      ) : (
        // 🔥 V5: Contenedor de mensajes con scroll infinito
        <div
          ref={chatContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto overscroll-contain py-2 scroll-smooth"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* 🔥 V5: Indicador de carga de mensajes antiguos */}
          {isLoadingOlder && (
            <div className="flex items-center justify-center py-3">
              <Loader2 size={18} className="animate-spin text-gray-400" />
              <span className="ml-2 text-xs text-gray-400">Carregando mensagens anteriores...</span>
            </div>
          )}

          {/* 🔥 V5: Indicador de que hay más mensajes (cuando no está cargando) */}
          {hasMore && !isLoadingOlder && (
            <div className="flex items-center justify-center py-2">
              <span className="text-[10px] text-gray-400 bg-gray-100 rounded-full px-3 py-0.5">
                Role para cima para ver mais
              </span>
            </div>
          )}

          {/* Mensajes agrupados por fecha */}
          <AnimatePresence mode="popLayout">
            {groupedMessages.map((group) => (
              <motion.div
                key={group.label}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <DateSeparator label={group.label} />
                {group.messages.map((msg, idx) => (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    isLast={idx === group.messages.length - 1}
                    onCopy={() => handleCopy(msg)}
                    onForward={() => handleForward(msg)}
                    onDelete={() => handleDelete(msg.id)}
                  />
                ))}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* 🔥 V5: Referencia para scroll to bottom */}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Botón scroll to bottom */}
      <AnimatePresence>
        {showScrollButton && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => scrollToBottom()}
            className="absolute bottom-20 right-4 z-10 p-2 bg-card rounded-full shadow-lg
                       text-gray-600 hover:text-gray-800 hover:shadow-xl transition-all"
            aria-label="Ir para última mensagem"
          >
            <ChevronDown size={20} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Error de envío */}
      {sendError && (
        <div className="px-4 py-2 bg-red-50 text-red-600 text-xs text-center border-t border-red-100">
          {sendError}
          <button
            onClick={() => setSendError(null)}
            className="ml-2 underline hover:text-red-800"
          >
            Fechar
          </button>
        </div>
      )}

      {/* 🔥 V4: Input SIEMPRE visible si hay cliente (tenga o no teléfono) */}
      <ChatInput
        onSend={handleSend}
        replyingTo={replyingTo}
        onCancelReply={() => setReplyingTo(null)}
        loading={sending}
        disabled={!client?.phone}
        clientName={client?.name || client?.clientName || ''}
        placeholder={client?.phone ? 'Digite uma mensagem...' : 'Cliente sem telefone cadastrado'}
      />
    </div>
  );
};

ChatView.displayName = 'ChatView';
export { ChatView };
export default ChatView;